<template>
    <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
        <q-card class="q-dialog-plugin" style="min-width: 350px">
            <q-card-section class="row items-center">
                <q-avatar icon="error" color="negative" text-color="white" />
                <span class="q-ml-sm text-h6">{{ ts('errorOccurred') }}</span>
            </q-card-section>

            <q-card-section class="q-pt-none">
                {{ ts('errorMessage') }}
            </q-card-section>

            <q-card-section v-if="props.error" class="q-pt-none">
                <div class="text-caption text-grey-7">{{ ts('errorDetail') }}</div>
                <pre class="error-detail q-pa-sm bg-grey-2 rounded-borders overflow-auto">{{ props.error }}</pre>
            </q-card-section>

            <q-card-actions align="right">
                <q-btn flat :label="ts('close')" color="primary" @click="onDialogOK" />
                <q-btn unelevated :label="ts('sendReport')" color="primary" @click="sendReport" />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script setup lang="ts">
import { useDialogPluginComponent } from 'quasar'
import { ts } from '@/plugins/i18nFormat'
import { version } from '@/../package.json'
import { useRepo } from 'pinia-orm'
import SettingsRepo from '@/orm/repositories/SettingsRepo'
import { Defaults, Options } from '@/orm/models/Setting'
import EventsRepo from '@/orm/repositories/EventsRepo'
import { useRoute } from 'vue-router'
import { computed } from 'vue'

interface Props {
    error?: string
}

const props = defineProps<Props>()

defineEmits([...useDialogPluginComponent.emits])

const { dialogRef, onDialogHide, onDialogOK } = useDialogPluginComponent()

const route = useRoute()

const appVersion = version
const loggedUserName = computed(() => useRepo(SettingsRepo).getOption(Options.AuthFullName, Defaults.AuthFullName))
const loggedUserRegId = computed(() => useRepo(SettingsRepo).getOption(Options.AuthRegId, Defaults.AuthRegId))
const lastDataUpdate = computed(() =>
    useRepo(SettingsRepo).getOption(Options.LastSailingDataUpdated, Defaults.LastSailingDataUpdated)
)

const currentEvent = computed(() => {
    const eventId = route.params.eventId as string

    if (!eventId) return null

    return useRepo(EventsRepo).find(eventId)
})

function sendReport() {
    const subject = encodeURIComponent('Error Report - Scoring Application')

    let info = `Version: ${appVersion}\n`
    info += `User: ${loggedUserName.value} (${loggedUserRegId.value})\n`
    info += `Last CSJ Data Update: ${lastDataUpdate.value ?? 'never'}\n`

    if (currentEvent.value) {
        info += `Event: ${currentEvent.value.title} (ctlId: ${currentEvent.value.ctlId})\n`
    } else {
        info += `Event: none\n`
    }

    const body = encodeURIComponent(
        `An error occurred in the application:\n\n${props.error}\n\n${info}\nEnvironment: ${window.location.href}\nUser Agent: ${navigator.userAgent}`,
    )
    window.location.href = `mailto:sorm@sailing.cz?subject=${subject}&body=${body}`
    onDialogOK()
}
</script>

<style scoped>
.error-detail {
    max-height: 200px;
    font-size: 0.8em;
    white-space: pre-wrap;
    word-break: break-all;
}
</style>
